"use client";

import { useCallback } from "react";
import {
    type CarouselApi,
    CarouselContentVisible,
    CarouselItem,
} from "~/components/ui/carousel";
import Carousel from "~/componentsDS/Carousel";
import Container from "~/componentsDS/Layout/Container";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Reveal from "~/componentsDS/Reveal";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

import ClinicianCard from "./ClinicianCard";
import { CLINICIANS } from "./data";

const CONTAINER_LAYOUT = {
    tabletBreakpoint: 768,
    desktopBreakpoint: 1280,
    wideBreakpoint: 1440,
    maxWidth: 1600,
    mobileInset: 24,
    tabletInset: 48,
    desktopInset: 64,
    wideInset: 80,
} as const;

const alignCarouselToContainer = (viewSize: number) => {
    if (viewSize > CONTAINER_LAYOUT.maxWidth) {
        return (
            (viewSize - CONTAINER_LAYOUT.maxWidth) / 2
            + CONTAINER_LAYOUT.wideInset
        );
    }

    if (viewSize >= CONTAINER_LAYOUT.wideBreakpoint) {
        return CONTAINER_LAYOUT.wideInset;
    }

    if (viewSize >= CONTAINER_LAYOUT.desktopBreakpoint) {
        return CONTAINER_LAYOUT.desktopInset;
    }

    if (viewSize >= CONTAINER_LAYOUT.tabletBreakpoint) {
        return CONTAINER_LAYOUT.tabletInset;
    }

    return CONTAINER_LAYOUT.mobileInset;
};

export default function CliniciansSection() {
    const sendEvent = useTrackEvent();

    const handleSelect = useCallback((api: CarouselApi) => {
        if (!api) return;

        const index = api.selectedScrollSnap();
        const clinician = CLINICIANS[index];
        if (!clinician) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "experienced_clinicians",
                meta_data: {
                    image_person_name: clinician.name,
                    image_count: index + 1,
                    product_name: "general",
                },
            },
        });
    }, [sendEvent]);

    return (
        <section className="overflow-hidden pt-16 pb-8 text-primary500 md:pb-16 lg:pt-24">
            <Container>
                <Reveal>
                    <Heading as="h2" size="sm" sizes={{ lg: "md" }} className="mb-4 lg:mb-[10px]">
                        Care led by <Heading as="i" font="featureDisplay" weight="400" className="text-[28px] lg:text-[36px]">experienced</Heading> clinicians
                    </Heading>
                </Reveal>
                <Paragraph className="mb-4 lg:mb-8">
                    Care at Inner Balance is led by licensed clinicians trained in women&apos;s health, hormone therapy, <br className="max-lg:hidden" />
                    and longevity medicine. Every treatment is prescribed and monitored by a medical professional.
                </Paragraph>

                <Carousel
                    loop={true}
                    desktopWheelGestures
                    onSelect={handleSelect}
                    opts={{
                        align: alignCarouselToContainer,
                        containScroll: "trimSnaps",
                        skipSnaps: false,
                    }}
                    className="left-1/2 w-screen -translate-x-1/2 [&>div:last-child]:hidden lg:[&>div:last-child]:flex"
                >
                    <CarouselContentVisible className="ml-0">
                        {CLINICIANS.map((clinician) => (
                            <CarouselItem
                                key={clinician.name}
                                className="mr-2 max-w-[352px] basis-[calc(100vw-48px)] pl-0 lg:max-w-none lg:basis-[360px]"
                            >
                                <ClinicianCard clinician={clinician} />
                            </CarouselItem>
                        ))}
                    </CarouselContentVisible>
                </Carousel>
            </Container>
        </section>
    );
}
